import { useCallback, useEffect, useRef, useState } from "react";

/**
 * One debounced, cancellable, race-proof search request per input.
 *
 * Every search box in the app had its own hand-rolled version of this, and each
 * was missing a different piece:
 *
 * - **No cancellation.** With the backend taking ~1.2 s and the debounce at
 *   300 ms, two or three requests were always in flight. Whichever *finished*
 *   last won, so the list regularly settled on the results of a query the user
 *   had already finished typing over. That is what "поиск глючит" means in
 *   practice: the right answer appears, then is replaced by an older wrong one.
 * - **Blanking on every keystroke.** `setItems(null)` before each request threw
 *   the whole list away and replaced it with a spinner, losing scroll position
 *   twice a second while typing. Here the previous results stay on screen,
 *   dimmed by `loading`, until the new ones arrive.
 * - **No minimum length.** One character matches most of the table and cannot
 *   use a trigram index, so the first two keystrokes were the most expensive
 *   queries in the system — and their results were never useful.
 * - **A permanently broken page after one failure.** A rejected promise with no
 *   `catch` left the list `null` forever. An error here is a state, and the next
 *   keystroke clears it.
 */
export interface LiveSearchOptions {
  /** Shortest query that is sent. Below it the search is idle. Default 2. */
  minLength?: number;
  /** Quiet period before firing. Default 250 ms. */
  delayMs?: number;
  /** Re-run when any of these change (filters, tabs, …). */
  deps?: readonly unknown[];
  /** When false, nothing is requested and the last result is kept. */
  enabled?: boolean;
  /** Run with an empty query too — for lists that show everything by default. */
  allowEmpty?: boolean;
}

export interface LiveSearchState<T> {
  data: T | null;
  loading: boolean;
  error: string | null;
  /** True while a request for a *different* query than `data` is in flight. */
  stale: boolean;
  reload: () => void;
}

export function useLiveSearch<T>(
  query: string,
  run: (query: string, signal: AbortSignal) => Promise<T>,
  options: LiveSearchOptions = {},
): LiveSearchState<T> {
  const {
    minLength = 2,
    delayMs = 250,
    deps = [],
    enabled = true,
    allowEmpty = true,
  } = options;

  const [data, setData] = useState<T | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [settledQuery, setSettledQuery] = useState<string | null>(null);

  // `run` is almost always an inline arrow, so its identity changes every
  // render. Holding it in a ref keeps it out of the effect's dependency list —
  // otherwise the search re-fires on every unrelated re-render, which is how
  // one of these boxes ended up issuing a request per render of its parent.
  const runRef = useRef(run);
  runRef.current = run;

  const seq = useRef(0);
  const inflight = useRef<AbortController | null>(null);
  const [nonce, setNonce] = useState(0);

  const trimmed = query.trim();
  const tooShort = trimmed.length > 0 && trimmed.length < minLength;
  const idle = !enabled || tooShort || (!allowEmpty && !trimmed);

  useEffect(() => {
    if (idle) {
      // Cancel whatever was running for the longer query the user just deleted.
      inflight.current?.abort();
      inflight.current = null;
      setLoading(false);
      return;
    }

    const timer = window.setTimeout(() => {
      inflight.current?.abort();
      const controller = new AbortController();
      inflight.current = controller;
      const ticket = ++seq.current;

      setLoading(true);
      runRef
        .current(trimmed, controller.signal)
        .then((result) => {
          // A response for a query the user has already typed past. Dropping it
          // here is the whole point: without this check it would overwrite the
          // newer result that has already landed.
          if (ticket !== seq.current) return;
          setData(result);
          setSettledQuery(trimmed);
          setError(null);
          setLoading(false);
        })
        .catch((e: unknown) => {
          if (ticket !== seq.current) return;
          // An abort is us cancelling, not a failure — showing an error for it
          // would flash "не удалось загрузить" on every keystroke.
          if (e instanceof DOMException && e.name === "AbortError") return;
          setError(e instanceof Error ? e.message : "Не удалось выполнить поиск");
          setLoading(false);
        });
    }, delayMs);

    return () => window.clearTimeout(timer);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [trimmed, idle, delayMs, nonce, ...deps]);

  // Abort the last request when the component goes away, so a closing modal
  // does not leave a request running and then set state on a dead component.
  useEffect(() => () => inflight.current?.abort(), []);

  const reload = useCallback(() => setNonce((n) => n + 1), []);

  return {
    data,
    loading,
    error,
    stale: loading && settledQuery !== trimmed,
    reload,
  };
}
